import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import adminApi from '../services/adminApi';

function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [mealPlans, setMealPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadUserData();
  }, [userId]);

  const loadUserData = async () => {
    try {
      setIsLoading(true);
      
      const [detailsRes, prefsRes, plansRes] = await Promise.all([
        adminApi.getUserDetails(userId),
        adminApi.getUserPreferences(userId),
        adminApi.getUserMealPlans(userId, { limit: 10 })
      ]);

      setUser(detailsRes.user);
      setStats(detailsRes.stats);
      setPreferences(prefsRes);
      setMealPlans(plansRes.mealPlans || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  const handleTogglePremium = async () => {
    try {
      await adminApi.updateUser(userId, { isPremium: !user.isPremium });
      setUser({ ...user, isPremium: !user.isPremium });
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  const handleToggleAdmin = async () => {
    try {
      await adminApi.updateUser(userId, { isAdmin: !user.isAdmin });
      setUser({ ...user, isAdmin: !user.isAdmin });
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading user details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-card">
        <p style={{ color: '#dc2626' }}>Error: {error}</p>
        <button className="admin-btn admin-btn-secondary" onClick={() => navigate('/admin/users')}>
          ← Back to Users
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <button 
          className="admin-btn admin-btn-secondary admin-btn-sm" 
          onClick={() => navigate('/admin/users')}
          style={{ marginBottom: '1rem' }}
        >
          ← Back to Users
        </button>
        <h1>{user?.email}</h1>
        <p>User details and meal plan history</p>
      </div>

      {/* User Info Card */}
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: '0 0 1rem 0' }}>User Information</h3>
            <table style={{ borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280' }}>Email:</td>
                  <td style={{ padding: '0.5rem 0' }}><strong>{user?.email}</strong></td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280' }}>Name:</td>
                  <td style={{ padding: '0.5rem 0' }}>{user?.name || '-'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280' }}>Phone:</td>
                  <td style={{ padding: '0.5rem 0' }}>{user?.phone || '-'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280' }}>Language:</td>
                  <td style={{ padding: '0.5rem 0' }}>{user?.language || 'en'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280' }}>Registered:</td>
                  <td style={{ padding: '0.5rem 0' }}>{formatDate(user?.createdAt)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280' }}>Auth Method:</td>
                  <td style={{ padding: '0.5rem 0' }}>
                    {user?.googleId ? (
                      <span className="admin-badge admin-badge-success">Google</span>
                    ) : user?.appleId ? (
                      <span className="admin-badge admin-badge-info">Apple</span>
                    ) : (
                      <span className="admin-badge admin-badge-warning">Email/OTP</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              {user?.isPremium ? (
                <span className="admin-badge admin-badge-success" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                  ⭐ Premium User
                </span>
              ) : (
                <span className="admin-badge admin-badge-info" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                  Free User
                </span>
              )}
            </div>
            {user?.isAdmin && (
              <div style={{ marginBottom: '0.5rem' }}>
                <span className="admin-badge admin-badge-warning" style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}>
                  🛠️ Site Admin
                </span>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
              <button 
                className={`admin-btn ${user?.isPremium ? 'admin-btn-danger' : 'admin-btn-primary'}`}
                onClick={handleTogglePremium}
              >
                {user?.isPremium ? 'Remove Premium' : 'Grant Premium'}
              </button>
              <button 
                className={`admin-btn ${user?.isAdmin ? 'admin-btn-danger' : 'admin-btn-secondary'}`}
                onClick={handleToggleAdmin}
              >
                {user?.isAdmin ? 'Remove Admin' : 'Grant Admin'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Info Card */}
      {(user?.isPremium || user?.stripeCustomerId) && (
        <div className="admin-card">
          <h3 style={{ margin: '0 0 1rem 0' }}>Subscription Details</h3>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280', width: '200px' }}>Plan:</td>
                <td style={{ padding: '0.5rem 0' }}>
                  <strong>{user?.stripePlanName || '-'}</strong>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280', width: '200px' }}>Status:</td>
                <td style={{ padding: '0.5rem 0' }}>
                  {user?.isPremium ? (
                    user?.premiumExpiresAt && new Date(user.premiumExpiresAt) < new Date() ? (
                      <span className="admin-badge admin-badge-danger">Expired</span>
                    ) : (
                      <span className="admin-badge admin-badge-success">Active</span>
                    )
                  ) : (
                    <span className="admin-badge admin-badge-warning">Inactive</span>
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280' }}>Expires:</td>
                <td style={{ padding: '0.5rem 0' }}>
                  {user?.premiumExpiresAt ? (
                    <span style={{ color: new Date(user.premiumExpiresAt) < new Date() ? '#dc2626' : undefined }}>
                      {formatDate(user.premiumExpiresAt)}
                    </span>
                  ) : (
                    <span style={{ color: '#6b7280' }}>-</span>
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280' }}>Stripe Customer ID:</td>
                <td style={{ padding: '0.5rem 0', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {user?.stripeCustomerId ? (
                    <a 
                      href={`https://dashboard.stripe.com/customers/${user.stripeCustomerId}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#2563eb', textDecoration: 'underline' }}
                    >
                      {user.stripeCustomerId}
                    </a>
                  ) : '-'}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem 1rem 0.5rem 0', color: '#6b7280' }}>Stripe Subscription ID:</td>
                <td style={{ padding: '0.5rem 0', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {user?.stripeSubscriptionId ? (
                    <a 
                      href={`https://dashboard.stripe.com/subscriptions/${user.stripeSubscriptionId}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#2563eb', textDecoration: 'underline' }}
                    >
                      {user.stripeSubscriptionId}
                    </a>
                  ) : '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Stats */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="label">Total Generated</div>
          <div className="value">{stats?.totalPlansGenerated || user?.totalPlansGenerated || 0}</div>
        </div>
        <div className="admin-stat-card">
          <div className="label">Saved Plans</div>
          <div className="value">{stats?.savedMealPlans || 0}</div>
        </div>
        <div className="admin-stat-card">
          <div className="label">First Plan</div>
          <div className="value" style={{ fontSize: '1rem' }}>
            {stats?.firstPlanDate ? new Date(stats.firstPlanDate).toLocaleDateString() : '-'}
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="label">Last Plan</div>
          <div className="value" style={{ fontSize: '1rem' }}>
            {stats?.lastPlanDate ? new Date(stats.lastPlanDate).toLocaleDateString() : '-'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          className={`admin-btn ${activeTab === 'overview' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
          onClick={() => setActiveTab('overview')}
        >
          Preferences
        </button>
        <button
          className={`admin-btn ${activeTab === 'mealplans' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
          onClick={() => setActiveTab('mealplans')}
        >
          Meal Plan History
        </button>
      </div>

      {/* Preferences Tab */}
      {activeTab === 'overview' && (
        <div className="admin-card">
          <h3 style={{ margin: '0 0 1rem 0' }}>User Preferences</h3>
          {preferences?.preferences ? (
            <table className="admin-table">
              <tbody>
                <tr>
                  <td style={{ width: '200px', fontWeight: 500 }}>Number of People</td>
                  <td>{preferences.preferences.numberOfPeople || 1}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Budget</td>
                  <td>
                    {preferences.preferences.budget?.label || 'Standard'}
                    {preferences.preferences.budget?.min && preferences.preferences.budget?.max && (
                      <span style={{ color: '#6b7280' }}>
                        {' '}({preferences.preferences.budget.currency}{preferences.preferences.budget.min} - {preferences.preferences.budget.currency}{preferences.preferences.budget.max})
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Cuisine</td>
                  <td>{preferences.preferences.selectedCuisine || 'Surprise Me'}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Dietary Preferences</td>
                  <td>
                    {preferences.preferences.dietaryPreferences?.length > 0 
                      ? preferences.preferences.dietaryPreferences.map(d => d.label).join(', ')
                      : 'None'}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p style={{ color: '#6b7280' }}>No preferences saved</p>
          )}
        </div>
      )}

      {/* Meal Plans Tab */}
      {activeTab === 'mealplans' && (
        <div className="admin-card">
          <h3 style={{ margin: '0 0 1rem 0' }}>Meal Plan History</h3>
          {mealPlans.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No meal plans found</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Meals</th>
                  <th>Status</th>
                  <th>Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {mealPlans.map(plan => (
                  <tr key={plan._id}>
                    <td>{formatDate(plan.createdAt)}</td>
                    <td>{plan.mealPlan?.length || 0} meals</td>
                    <td>
                      {plan.isSaved ? (
                        <span className="admin-badge admin-badge-success">Saved</span>
                      ) : (
                        <span className="admin-badge admin-badge-warning">Draft</span>
                      )}
                    </td>
                    <td>{plan.groceryList?.totalCost || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default UserDetailPage;
